// Store / catalog contracts returned by the REST API.
import type { Cover, DeviceKind, PlayMode } from './protocol.ts';

export interface CategoryDto {
  id: string;
  name: string;
  icon: string;
  description: string;
  sortOrder: number;
  gameCount?: number;
}

export interface GameSummaryDto {
  id: string;
  slug: string;
  title: string;
  tagline: string;
  cover: Cover;
  priceCents: number;
  currency: string;
  minPlayers: number;
  maxPlayers: number;
  durationMin: number;
  devices: DeviceKind[];
  playMode: PlayMode;
  categories: string[];
  mechanics: string[];
  ageRating: string;
  featured: boolean;
  source: 'official' | 'community';
  rating: number | null;
  ratingCount: number;
  playCount: number;
  creatorName?: string | null;
  owned?: boolean;
  /** Only in the signed-in user's library: how long they have played it. */
  playSeconds?: number;
}

/** One game in a user's play-time breakdown (profile). */
export interface PlayTimeGameDto {
  gameId: string;
  slug: string;
  title: string;
  cover: Cover;
  seconds: number;
  plays: number;
  lastPlayedAt: number;
}

/** A signed-in player's wins and recent games (profile page). Places only count for games played to the end. */
export interface PlayRecordDto {
  played: number;
  wins: number;
  podiums: number;
  /** Wins out of games with a place, in percent. */
  winRate: number;
  perGame: { slug: string; title: string; cover: Cover; played: number; wins: number; best: number }[];
  /** Win rate per line-up: 'tek' (everyone alone) or a team format like '2v2'. */
  byFormat: { format: string; played: number; wins: number; winRate: number }[];
  /** People they played with most often (number of plays, not of different games). */
  playedWith: { userId: string; name: string; plays: number; sameTeam: number; wonTogether: number }[];
  recent: { slug: string; title: string; cover: Cover; role: 'host' | 'player'; rank: number | null; ranked: number | null; aborted: boolean; seconds: number; endedAt: number }[];
}

export interface PlayTimeDto {
  totalSec: number;
  plays: number;
  games: number;
  perGame: PlayTimeGameDto[];
}

// ---- Friends and invites ------------------------------------------------------

/** How another account relates to me. */
export type FriendRelation = 'self' | 'friends' | 'requested' | 'incoming' | 'none';

/** A live room as friends see it. */
export interface FriendRoomDto {
  code: string;
  gameTitle: string;
  gameCover: Cover;
  status: 'lobby' | 'playing' | 'ended';
  players: number;
  maxPlayers: number;
  /** New players can join right now. */
  accepting: boolean;
  /** The friend's place in it. */
  role: 'host' | 'player';
  /** I am in this room too (so there is nothing to join). */
  withMe: boolean;
}

export interface FriendDto {
  id: string;
  displayName: string;
  since: number;
  room: FriendRoomDto | null;
  /** Total play time and number of plays (hosted or joined while signed in). */
  playSeconds: number;
  plays: number;
  /** Their most played game. */
  favorite: { title: string; slug: string; cover: Cover } | null;
  lastPlayedAt: number | null;
  /** Friends we both have. */
  mutual: number;
  /** Games they won (played to the end, first place). */
  wins: number;
}

export interface FriendRequestDto {
  id: string;
  displayName: string;
  at: number;
}

export interface FriendsDto {
  friends: FriendDto[];
  incoming: FriendRequestDto[];
  outgoing: FriendRequestDto[];
}

export interface LobbyInviteDto {
  id: string;
  fromName: string;
  createdAt: number;
  room: FriendRoomDto;
}

/** A signed-in person in the same room, with whether we are friends. */
export interface RoomPersonDto {
  /** 'host' or the seat's player id; used to send a request without exposing account ids. */
  key: string;
  name: string;
  avatar: string | null;
  relation: FriendRelation;
}

/** One of my friends in the room's invite list. */
export interface InviteTargetDto {
  id: string;
  displayName: string;
  invited: boolean;
  inRoom: boolean;
  /** Another room they are in right now, if any. */
  elsewhere: FriendRoomDto | null;
}

export interface RoomSocialDto {
  people: RoomPersonDto[];
  friends: InviteTargetDto[];
}

export interface SocialSummaryDto {
  incoming: number;
  invites: number;
}

export interface ReviewDto {
  id: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: number;
}

/** A picture in a game's store gallery. Width and height are the file's own pixels. */
export interface GameImageDto {
  id: string;
  url: string;
  caption: string;
  width: number;
  height: number;
}

/** The admin's view of a gallery picture. */
export interface AdminGameImageDto extends GameImageDto {
  size: number;
  mime: string;
  createdAt: number;
}

export interface GameDetailDto extends GameSummaryDto {
  description: string;
  engine: string;
  reviews: ReviewDto[];
  maxConcurrentSessions: number;
  /** Store gallery, in the order the admin set. */
  images: GameImageDto[];
}

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  status: 'active' | 'banned';
  createdAt: number;
  /** False for accounts opened with Google that never set a password. */
  hasPassword?: boolean;
  /** A Google account is linked (can sign in with Google). */
  google?: boolean;
}

export interface AuthResponse {
  token: string;
  user: UserDto;
}

export interface StoreHomeDto {
  device: DeviceKind;
  featured: GameSummaryDto[];
  recommended: GameSummaryDto[];
  popular: GameSummaryDto[];
  newest: GameSummaryDto[];
  community: GameSummaryDto[];
  rows: { category: CategoryDto; games: GameSummaryDto[] }[];
}

export interface CreateSessionResponse {
  sessionId: string;
  code: string;
  hostToken: string;
}

export interface JoinSessionResponse {
  sessionId: string;
  playerId: string;
  playerToken: string;
}

export interface SessionLookupDto {
  sessionId: string;
  code: string;
  status: 'lobby' | 'playing' | 'ended';
  gameTitle: string;
  cover: Cover;
  playerCount: number;
  maxPlayers: number;
  acceptingPlayers: boolean;
  /** Players whose phone is currently offline: someone who lost their seat can take it back. */
  rejoin: { id: string; name: string; avatar: string }[];
}

/** Play time as people say it: "45 dk", "3 sa 20 dk", "128 saat". Under a minute rounds up to 1 dk. */
export function formatPlaytime(seconds: number): string {
  if (seconds <= 0) return '0 dk';
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} dk`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h >= 100) return `${h.toLocaleString('tr-TR')} saat`;
  return m ? `${h} sa ${m} dk` : `${h} saat`;
}

export function formatPrice(cents: number, currency = 'TRY'): string {
  if (cents <= 0) return 'Ücretsiz';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(cents / 100);
}
